/**
 * Mana Pricing results — every picked lender in one table, grouped by term × buydown program.
 *
 * Rate-sheet rows arrive priced from the API. UWM rows are built in the page from UWM's live
 * quote (buildUwmOptionRows), so the CYP incentive re-prices and re-stars them instantly, as in
 * the Pricing module. UWM prices show 100-based (100 − UWM's final price %) so they compare with
 * the sheets. Each source keeps its own breakdown: UWM's percent-and-dollar table with the
 * buydown LLPA and CYP lines, the rate sheets' LLPA list.
 *
 * Every option is shown at its best rate (★), lowest ★ rate first; equal ★ rates rank by the
 * higher net lender credit (ranking.ts). Options with no ★ follow at the desired-price rate.
 */
import { Fragment, useMemo, useState } from 'react';
import {
  MANA_SOURCE_LABELS,
  PRICING_COMP_CATEGORY_LABELS,
  REALTOR_COLUMNS,
  REALTOR_ROWS,
  realtorPriceFloor,
  type RealtorColumnKey,
  type RealtorPicks,
  type RealtorRowKey,
  uwmBuydownForChoice,
  uwmCypAdj,
  uwmEffectiveFinal,
  uwmLpCompAdj,
  uwmProductName,
  uwmTempBuydownAdj,
  type BuydownChoice,
  type ManaPricingSource,
  type PricingCompCategory,
  type UwmBuydownSel,
  type UwmLadderPoint,
  type UwmOptionRow,
  type UwmPricePoint,
} from '@mana/shared';
import { Badge } from '../../ui/Card.js';
import { Button } from '../../ui/Button.js';
import type { LadderPoint } from '../OtherLendersResults.js';
import { bestRateOrder, netLenderCredit, type RankCriteria } from './ranking.js';
import { fmtMoney0, fmtPct3, fmtPrice, fmtWhen, type QuoteApiResponse, type SheetQuoteRow } from './shared.js';
import { fmtCT, fmtSheetStamp } from '../../lib/time.js';

interface ResultPoint {
  rate: number;
  price: number;
  discountPct: number;
  discountDollars: number;
  apr: number;
  pi: number;
  mi: number | null;
  buydownCost: number | null;
  capped: boolean;
  sheet?: LadderPoint;
  uwm?: UwmLadderPoint;
}

/**
 * What an option competes as: its loan type family and amortization. A scenario that priced
 * several (Conventional + FHA, Fixed + ARM) groups by term × loan type × buydown, so an ARM or an
 * FHA rate is never a Conventional fixed group's best rate; with one, the groups are unchanged.
 * Jumbo competes as Conventional (UWM prices jumbo loans under its Conventional loan type).
 */
const LOAN_FAMILIES = ['conventional', 'fha', 'va', 'usda', 'dscr'] as const;
type LoanFamily = (typeof LOAN_FAMILIES)[number];
const LOAN_FAMILY_LABEL: Record<LoanFamily, string> = { conventional: 'Conventional', fha: 'FHA', va: 'VA', usda: 'USDA', dscr: 'DSCR' };
interface LoanKind {
  key: string;
  label: string;
  order: number;
}
/** A lender row's family from the loan type it was priced as ("FHA" → fha; Jumbo groups with Conventional). */
function sheetFamilyOf(loanType: string | undefined): LoanFamily {
  const t = (loanType ?? '').toLowerCase();
  return t === 'dscr' || t === 'fha' || t === 'va' || t === 'usda' ? t : 'conventional';
}
function loanKind(family: LoanFamily, amortization: 'fixed' | 'arm' | undefined): LoanKind {
  const arm = amortization === 'arm';
  return { key: `${family}:${arm ? 'arm' : 'fixed'}`, label: `${LOAN_FAMILY_LABEL[family]} ${arm ? 'ARM' : 'Fixed'}`, order: LOAN_FAMILIES.indexOf(family) * 2 + (arm ? 1 : 0) };
}

interface ResultRow {
  kind: 'sheet' | 'uwm';
  loanKind: LoanKind;
  optionId: string;
  lender: string;
  product: string;
  termYears: number;
  buydown: BuydownChoice | null;
  /** The rate the row shows: its ★ point, else the point priced closest to the desired price. */
  headline: ResultPoint;
  star: ResultPoint | null;
  /** The desired-price point (shown only when no rate meets ★). */
  pick: ResultPoint;
  starred: boolean;
  /** Headline lender credit after closing costs, buydown escrow and a borrower-paid comp (ranks equal ★ rates). */
  netCredit: number;
  ladder: ResultPoint[];
  lockPeriod: number;
  sheetRow?: SheetQuoteRow;
  uwmRow?: UwmOptionRow;
}

type SortKey = 'price' | 'rate' | 'apr' | 'pi' | 'product';

/** Headline = the ★ point when one qualifies, else the desired-price pick. */
function rankFields(ladder: ResultPoint[], starRate: number | null, pickRate: number, criteria: RankCriteria | null, borrowerPaidComp: boolean) {
  const star = starRate != null ? (ladder.find((p) => p.rate === starRate) ?? null) : null;
  const pick = ladder.find((p) => p.rate === pickRate) ?? ladder[0]!;
  const headline = star ?? pick;
  return { star, pick, headline, starred: star != null, netCredit: netLenderCredit(headline, criteria, borrowerPaidComp) };
}

function fromSheet(r: SheetQuoteRow, criteria: RankCriteria | null, borrowerPaidComp: boolean): ResultRow {
  const ladder: ResultPoint[] = r.ladder.map((p) => ({
    rate: p.rate,
    price: p.price,
    discountPct: p.discountPct,
    discountDollars: p.discountDollars,
    apr: p.apr,
    pi: p.pi,
    mi: null,
    buydownCost: p.buydownCost,
    capped: p.capped,
    sheet: p,
  }));
  return {
    kind: 'sheet',
    loanKind: loanKind(sheetFamilyOf(r.loanType), r.amortization),
    optionId: r.optionId,
    lender: r.investor,
    product: r.product,
    termYears: r.termYears,
    buydown: r.buydown as BuydownChoice | null,
    ...rankFields(ladder, r.starRate, r.rate, r.criteria !== undefined ? r.criteria : criteria, borrowerPaidComp),
    ladder,
    lockPeriod: r.lockPeriod,
    sheetRow: r,
  };
}

function fromUwm(r: UwmOptionRow, criteria: RankCriteria | null, borrowerPaidComp: boolean): ResultRow {
  const ladder: ResultPoint[] = r.ladder.map((p) => ({
    rate: p.rate,
    price: p.price,
    discountPct: p.discountPct,
    discountDollars: p.discountDollars,
    apr: p.apr,
    pi: p.pi,
    mi: p.mi,
    buydownCost: p.buydownCost,
    capped: false,
    uwm: p,
  }));
  return {
    kind: 'uwm',
    loanKind: loanKind(r.loanFamily ?? 'conventional', r.amortization),
    optionId: r.optionId,
    lender: r.investor,
    product: r.product,
    termYears: r.termYears,
    buydown: r.buydown,
    ...rankFields(ladder, r.starRate, r.rate, r.criteria !== undefined ? r.criteria : criteria, borrowerPaidComp),
    ladder,
    lockPeriod: r.lockPeriod,
    uwmRow: r,
  };
}

/** Signed credit/cost in dollars: green credit, red cost. */
function CreditCell({ pct, dollars, compact }: { pct: number; dollars: number; compact?: boolean }) {
  const cost = pct < -0.0005;
  const zero = Math.abs(pct) <= 0.0005;
  const tone = cost ? 'text-rose-600' : zero ? 'text-slate-500' : 'text-emerald-600';
  const main = cost ? `(${fmtMoney0(Math.abs(dollars))})` : zero ? '$0' : `+${fmtMoney0(Math.abs(dollars))}`;
  return (
    <span className={tone} title={cost ? 'Cost to the borrower' : zero ? 'Par' : 'Lender credit'}>
      <span className="font-medium tabular-nums">{main}</span>
      {!compact && <span className="ml-1 text-[10px] uppercase tracking-wide opacity-80">{cost ? 'cost' : zero ? 'par' : 'credit'}</span>}
    </span>
  );
}

function AddButton({ added, onClick, small }: { added: boolean; onClick: () => void; small?: boolean }) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      title={added ? 'Already in the borrower options' : 'Add this option for the borrower'}
      className={
        (small ? 'px-1.5 py-0.5 text-[11px] ' : 'px-2 py-1 text-xs ') +
        'rounded-md font-semibold transition-colors ' +
        (added ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200' : 'bg-brand-navy-50 text-brand-navy-700 ring-1 ring-brand-navy-200 hover:bg-brand-navy-100')
      }
    >
      {added ? '✓' : '+'}
    </button>
  );
}

export function ManaResults({
  quote,
  pricedAt,
  uwmRows,
  uwmLoanAmount,
  cypBps,
  onAddSheet,
  onAddUwm,
  inTray,
  onRefreshSheets,
  refreshing,
  fieldLabel,
  realtor,
}: {
  quote: QuoteApiResponse;
  pricedAt: Date;
  uwmRows: UwmOptionRow[];
  uwmLoanAmount: number;
  cypBps: number;
  onAddSheet: (row: SheetQuoteRow, point: LadderPoint) => void;
  onAddUwm: (row: UwmOptionRow, point: UwmLadderPoint) => void;
  inTray: (optionId: string, rate: number) => boolean;
  onRefreshSheets?: () => void;
  refreshing?: boolean;
  /** Readable name for a UWM field error key. */
  fieldLabel: (key: string) => string;
  /** Pinning rates for the Realtor Marketing table (absent = no pin buttons). */
  realtor?: RealtorControls;
}) {
  // Default order = best rate: lowest ★ rate first, equal rates by the higher net lender credit.
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({ key: 'rate', dir: 1 });
  const [open, setOpen] = useState<Set<string>>(new Set());
  // Term × buydown groups start collapsed; each header shows the group's best option until expanded.
  const [openGroups, setOpenGroups] = useState<Set<string>>(new Set());
  const [showHow, setShowHow] = useState(false);
  const [showIneligible, setShowIneligible] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const { uwm, sheets } = quote;
  const sheetQuote = sheets.status === 'ok' ? sheets.quote : null;
  /** Sources whose answer is still on its way (the slow engines come in a second step). */
  const pending = (quote.pending ?? []).map((l) => MANA_SOURCE_LABELS[l as ManaPricingSource] ?? l);

  const rows = useMemo(() => {
    const sheetBp = sheetQuote?.scenario.compType === 'BP';
    const uwmCriteriaForRank = uwm.status === 'ok' ? uwm.criteria : null;
    const uwmBp = uwm.status === 'ok' ? uwm.lpCompPct == null : true;
    return [
      ...(sheetQuote?.rows ?? []).map((r) => fromSheet(r, sheetQuote?.criteria ?? null, sheetBp)),
      ...uwmRows.map((r) => fromUwm(r, uwmCriteriaForRank, uwmBp)),
    ];
  }, [sheetQuote, uwmRows, uwm]);

  // One group per term × buydown program. "Best rate" = the group's first option in best-rate
  // order (lowest ★ rate; equal rates → higher net lender credit), across every lender.
  const groups = useMemo(() => {
    const ranked = (r: ResultRow) => ({ starred: r.starred, rate: r.headline.rate, netCredit: r.netCredit, lender: r.lender, product: r.product });
    const value = (r: ResultRow): number | string =>
      sort.key === 'product' ? `${r.lender} ${r.product}` : sort.key === 'pi' ? r.headline.pi : sort.key === 'apr' ? r.headline.apr : r.headline.price;
    const cmp = (a: ResultRow, b: ResultRow) => {
      if (sort.key === 'rate') return bestRateOrder(ranked(a), ranked(b), sort.dir);
      const av = value(a);
      const bv = value(b);
      const c = typeof av === 'number' && typeof bv === 'number' ? av - bv : String(av).localeCompare(String(bv));
      return c * sort.dir || bestRateOrder(ranked(a), ranked(b));
    };
    const multiKind = new Set(rows.map((r) => r.loanKind.key)).size > 1;
    const map = new Map<string, { key: string; termYears: number; loanKind: LoanKind | null; buydown: BuydownChoice | null; rows: ResultRow[]; bestId: string | null }>();
    for (const r of rows) {
      const key = `${r.termYears}|${multiKind ? r.loanKind.key : ''}|${r.buydown?.key ?? 'none'}`;
      let g = map.get(key);
      if (!g) {
        g = { key, termYears: r.termYears, loanKind: multiKind ? r.loanKind : null, buydown: r.buydown, rows: [], bestId: null };
        map.set(key, g);
      }
      g.rows.push(r);
    }
    const list = [...map.values()].sort(
      (a, b) => b.termYears - a.termYears || (a.loanKind?.order ?? 0) - (b.loanKind?.order ?? 0) || (a.buydown?.key ?? '').localeCompare(b.buydown?.key ?? ''),
    );
    for (const g of list) {
      const best = [...g.rows].sort((a, b) => bestRateOrder(ranked(a), ranked(b)))[0];
      g.bestId = best?.starred ? best.optionId : null;
      g.rows.sort(cmp);
    }
    return list;
  }, [rows, sort]);

  const toggleSort = (key: SortKey) => setSort((cur) => (cur.key === key ? { key, dir: cur.dir === 1 ? -1 : 1 } : { key, dir: key === 'price' ? -1 : 1 }));
  const toggleOpen = (id: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const toggleGroup = (key: string) =>
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const allGroupsOpen = groups.length > 0 && groups.every((g) => openGroups.has(g.key));

  const s = sheetQuote?.scenario;
  const uwmLpCompPct = uwm.status === 'ok' ? uwm.lpCompPct : null;
  // Lender-paid comp percent across the priced lenders (1.000% for every lender).
  const lpPcts = [...new Set([...(sheetQuote?.sheets ?? []).map((sh) => sh.lpCompPct), ...(uwmLpCompPct != null ? [uwmLpCompPct] : [])].map((p) => p.toFixed(3)))];
  const lpPctLabel = lpPcts.length ? `${lpPcts.join(' / ')}%` : '';
  const chips: string[] = s
    ? [
        `${s.loanType} · ${s.lienPosition} lien`,
        s.loanPurpose || '—',
        `${fmtMoney0(s.loanAmount)} / ${fmtMoney0(s.propertyValue)} · ${s.cltv != null ? `${s.cltv.toFixed(1)}% CLTV` : `${s.ltv.toFixed(1)}% LTV`}`,
        `FICO ${s.fico}`,
        `${(s.terms?.length ? s.terms : [s.termYears]).join(' / ')} yr ${(s.amortizations?.length ? s.amortizations : [s.amortization]).map((a) => (a === 'arm' ? 'ARM' : 'fixed')).join(' + ')}`,
        ...((s.buydowns ?? []).some((b) => b !== 'None') ? [`Buydown ${s.buydowns.map((b) => (b === 'None' ? 'none' : b)).join(' / ')}`] : []),
        `${s.lockPeriod}-day lock`,
        s.state ?? 'state —',
        s.compType === 'BP' ? 'Comp BP (borrower-paid)' : `Comp LP ${lpPctLabel} netted out`,
        ...(uwm.status === 'ok' && cypBps ? [`UWM CYP ${cypBps} bps`] : []),
      ]
    : uwm.status === 'ok'
      ? [uwmLpCompPct != null ? `Comp LP ${uwmLpCompPct.toFixed(3)}% netted out` : 'Comp BP (borrower-paid)', ...(cypBps ? [`UWM CYP ${cypBps} bps`] : [])]
      : [];
  const sourceLine = [
    ...(uwm.status === 'ok' ? [`UWM live quote ${fmtWhen(uwm.quotedAt)}`] : []),
    ...(sheetQuote?.sheets ?? []).map((sh) => (sh.family === 'live' ? `${sh.investor} live quote ${fmtWhen(sh.fetchedAt)}` : `${sh.investor} · sheet ${fmtSheetStamp(sh)}`)),
  ].join(' · ');
  const sheetCriteria = sheetQuote?.criteria ?? null;
  const uwmCriteria = uwm.status === 'ok' ? uwm.criteria : null;
  const categoryLabel = (c: string | null | undefined) => (c ? (PRICING_COMP_CATEGORY_LABELS[c as PricingCompCategory] ?? c) : '');
  const ineligible = [
    ...(sheetQuote?.ineligible ?? []).map((r) => ({ key: `s|${r.productId}|${r.product}|${r.issue}`, lender: r.investor, product: r.product, issue: r.issue })),
    ...(uwm.status === 'ok' ? (uwm.result.invalidQuoteItems ?? []) : []).map((q, i) => ({
      key: `u|${i}`,
      lender: 'UWM',
      product: uwmProductName(q),
      issue: (q.failures?.flatMap((f) => f.reasons ?? []) ?? q.invalidReasons ?? []).join('; ') || 'not eligible',
    })),
  ];
  const skippedNote = (status: { status: string; reason?: string }) =>
    status.status === 'skipped' && status.reason && !/not picked|no rate-sheet lender picked/i.test(status.reason) ? status.reason : null;

  const th = (label: string, key?: SortKey, extra = '') => (
    <th className={'px-2 py-2 font-semibold ' + extra + (key ? ' cursor-pointer select-none hover:text-brand-navy-700' : '')} onClick={key ? () => toggleSort(key) : undefined} title={key ? 'Sort' : undefined}>
      {label}
      {key && sort.key === key && <span className="ml-0.5 text-slate-400">{sort.dir === 1 ? '▲' : '▼'}</span>}
    </th>
  );

  const skippedNotes = [skippedNote(uwm), skippedNote(sheets)].filter((n): n is string => !!n);
  const pricingNotes = [...(sheetQuote?.unsupported ?? []).map((u) => `Not priced: ${u}`), ...(sheetQuote?.warnings ?? []), ...(uwm.status === 'ok' ? (uwm.notes ?? []) : [])];
  const hasAlerts = uwm.status === 'error' || sheets.status === 'error' || skippedNotes.length > 0 || pricingNotes.length > 0;

  return (
    <div className="surface-card">
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-b border-slate-100 px-5 py-3">
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-brand-navy-800">
            Results
            <span className="ml-1 font-normal text-slate-500">
              · {rows.length} option{rows.length === 1 ? '' : 's'} · priced {fmtCT(pricedAt)}
            </span>
            {pending.length > 0 && (
              <span className="ml-2 inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2 py-0.5 align-middle text-[11px] font-medium text-amber-700 ring-1 ring-amber-200" role="status">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-amber-500" aria-hidden />
                {pending.join(', ')} pricing…
              </span>
            )}
          </h3>
          {chips.length > 0 && (
            <p className="mt-0.5 truncate text-xs text-slate-500" title={[chips.join(' · '), sourceLine].filter(Boolean).join('\n')}>
              {chips.join(' · ')}
            </p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {groups.length > 0 && (
            <Button size="sm" variant="ghost" onClick={() => setOpenGroups(allGroupsOpen ? new Set() : new Set(groups.map((g) => g.key)))} title={allGroupsOpen ? 'Collapse every term × buydown group' : 'Expand every term × buydown group'}>
              {allGroupsOpen ? 'Collapse all' : 'Expand all'}
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={() => setShowHow((v) => !v)} aria-expanded={showHow}>
            {showHow ? 'Hide pricing rules' : 'How this is priced'}
          </Button>
          {/* Live-priced lenders are asked afresh on every quote; only sheet-priced ones (Spring EQ) have a sheet to refresh. */}
          {onRefreshSheets && sheets.status !== 'skipped' && !(sheetQuote?.sheets.length && sheetQuote.sheets.every((sh) => sh.family === 'live')) && (
            <Button size="sm" variant="secondary" disabled={refreshing} onClick={onRefreshSheets} title="Pull any new rate-sheet version from the lender portals, then re-price">
              {refreshing ? 'Refreshing…' : 'Refresh sheets'}
            </Button>
          )}
        </div>
      </div>

      {hasAlerts && (
      <div className="space-y-1.5 border-b border-slate-100 px-5 py-2.5">
        {uwm.status === 'error' && (
          <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
            <p>
              <span className="font-semibold">UWM wasn't priced:</span> {uwm.message}
              {uwm.retryAfterSeconds ? ` Try again in ${uwm.retryAfterSeconds}s.` : ''}
            </p>
            {uwm.fieldErrors && Object.keys(uwm.fieldErrors).length > 0 && (
              <ul className="mt-1 list-disc space-y-0.5 pl-5">
                {Object.entries(uwm.fieldErrors).map(([k, msgs]) => (
                  <li key={k}>
                    <span className="font-medium">{fieldLabel(k)}</span>: {msgs.join(' ')}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
        {sheets.status === 'error' && (
          <p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
            <span className="font-semibold">Rate sheets weren't priced:</span> {sheets.message}
          </p>
        )}
        {skippedNotes.map((n) => (
          <p key={n} className="text-xs text-slate-500">
            {n}
          </p>
        ))}
        {pricingNotes.length > 0 && (
          <div>
            <button type="button" onClick={() => setShowNotes((v) => !v)} aria-expanded={showNotes} className="text-xs font-medium text-amber-700 hover:underline">
              <span className="mr-1 inline-block w-2.5">{showNotes ? '▾' : '▸'}</span>
              {pricingNotes.length} note{pricingNotes.length === 1 ? '' : 's'} about this scenario
            </button>
            {showNotes && (
              <ul className="mt-1 list-disc space-y-0.5 pl-8 text-xs text-amber-800">
                {pricingNotes.map((n) => (
                  <li key={n}>{n}</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
      )}

      {showHow && (
        <div className="mx-5 my-3 space-y-2 rounded-lg bg-slate-50 px-4 py-3 text-[11px] text-slate-600">
          {sourceLine && (
            <p>
              <span className="font-semibold text-slate-700">Priced on:</span> {sourceLine}
            </p>
          )}
          {uwm.status === 'ok' &&
            (uwmCriteria ? (
              <p>
                <span className="font-semibold text-brand-gold-700">★ UWM</span>{' '}
                {uwm.lpCompPct != null ? (
                  <>
                    lowest rate whose lender credit (net of the {(uwm.lpCompPct ?? 0).toFixed(3)}% lender-paid comp, after a lender-paid buydown's LLPA{cypBps ? ` and CYP ${cypBps} bps` : ''}) covers your{' '}
                    <span className="font-medium text-slate-700">{fmtMoney0(uwmCriteria.closing)} closing costs</span>
                    {uwm.category ? ` (${categoryLabel(uwm.category)} · comp paid by the lender)` : ' (comp paid by the lender)'}.
                  </>
                ) : (
                  <>
                    lowest rate whose lender credit (after a lender-paid buydown's LLPA{cypBps ? ` and CYP ${cypBps} bps` : ''}) covers your{' '}
                    <span className="font-medium text-slate-700">{fmtMoney0(uwmCriteria.comp)} comp</span> after <span className="font-medium text-slate-700">{fmtMoney0(uwmCriteria.closing)} closing costs</span>
                    {uwm.category ? ` (${categoryLabel(uwm.category)} · borrower-paid comp)` : ' (borrower-paid comp)'}.
                  </>
                )}{' '}
                A seller/borrower-paid buydown's concession counts as a closing cost.
              </p>
            ) : (
              <p>No UWM ★ for this scenario — the comp criteria covers purchase / rate-term refi on Conventional, FHA, VA and HELOC.</p>
            ))}
          {sheetQuote && sheetQuote.rows.length > 0 &&
            (sheetCriteria ? (
              <p>
                <span className="font-semibold text-brand-gold-700">★ Rate sheets</span>{' '}
                {sheetQuote.scenario.compType === 'LP' ? (
                  <>
                    lowest rate whose lender credit (net of the lender-paid comp) covers your <span className="font-medium text-slate-700">{fmtMoney0(sheetCriteria.closing)} closing costs</span> ({categoryLabel(sheetCriteria.category)} · comp paid by the lender).
                  </>
                ) : (
                  <>
                    lowest rate whose lender credit covers your <span className="font-medium text-slate-700">{fmtMoney0(sheetCriteria.comp)} comp</span> after{' '}
                    <span className="font-medium text-slate-700">{fmtMoney0(sheetCriteria.closing)} closing costs</span> ({categoryLabel(sheetCriteria.category)} · borrower-paid comp).
                  </>
                )}{' '}
                A seller/borrower-paid buydown escrow counts as a closing cost.
              </p>
            ) : (
              <p>No rate-sheet ★ for this scenario — set your amounts under Integrations → Set Comp and Closing Costs.</p>
            ))}
          <p>
            Each option shows its ★ rate, lowest first; at the same ★ rate the higher net lender credit (after your closing costs, a buydown escrow and a
            borrower-paid comp) ranks first. An option with no ★ is listed last at the rate priced closest to the desired price.
          </p>
          <ul className="list-disc space-y-1 pl-4">
          {uwm.status === 'ok' && (
            <>
              <li>UWM prices live through its Instant Price Quote API; its final price is used as returned. UWM quotes the same price whether the comp is lender-paid or borrower-paid, so for a lender-paid comp Mana nets 1.000% of the loan out of every UWM price — the same lender-paid comp as every rate-sheet lender. ★ follows the rate sheets' rule: lender-paid comp — the credit (after the 1%) covers your closing costs; borrower-paid comp — it covers your closing costs and your comp.</li>
              <li>UWM lender-paid buydowns add a fixed LLPA of the loan amount: 1/0 0.875%, 1/1 1.5%, 2/1 2.375%, 3/2/1 4.5%. Seller/borrower-paid buydowns add the concession Σ years (note P&amp;I − bought-down P&amp;I) × 12 as a closing cost.</li>
              <li>CYP (Control Your Price) is Mana's basis-point credit on every UWM final price; it is not sent to UWM. UWM prices show here as 100 − UWM's final price %.</li>
            </>
          )}
          {(sheetQuote?.assumptions ?? []).map((a) => (
            <li key={a}>{a}</li>
          ))}
          <li>APR shown for every lender = note rate + 0.600%. P&amp;I excludes MI, taxes and insurance; UWM rows show UWM's MI estimate.</li>
          </ul>
        </div>
      )}

      <div className="overflow-x-auto">
        {groups.length === 0 ? (
          <p className="px-5 py-5 text-sm text-slate-500">No eligible products for these inputs.</p>
        ) : (
          <table className="w-full min-w-[640px] text-sm">
            <thead className="border-b border-slate-100 bg-slate-50/60 text-left text-[10px] uppercase tracking-[0.12em] text-slate-500">
              <tr>
                {th('Lender · Product', 'product', 'pl-5')}
                {th('Rate', 'rate', 'text-right')}
                {th('APR', 'apr', 'text-right')}
                {th('Price', 'price', 'text-right')}
                {th('Cost / credit', undefined, 'text-right')}
                {th('P&I', 'pi', 'text-right')}
                <th className="py-2 pl-2 pr-4" />
              </tr>
            </thead>
            <tbody>
              {groups.map((g) => {
                const isGroupOpen = openGroups.has(g.key);
                // The group's best option (lowest ★ rate; equal rates → higher net credit), shown while collapsed.
                const best = (g.bestId ? g.rows.find((r) => r.optionId === g.bestId) : null) ?? [...g.rows].sort((a, b) => bestRateOrder({ starred: a.starred, rate: a.headline.rate, netCredit: a.netCredit, lender: a.lender, product: a.product }, { starred: b.starred, rate: b.headline.rate, netCredit: b.netCredit, lender: b.lender, product: b.product }))[0] ?? null;
                return (
                <Fragment key={g.key}>
                  <tr className="cursor-pointer bg-slate-100/80 hover:bg-slate-200/70" onClick={() => toggleGroup(g.key)} title={isGroupOpen ? 'Collapse this group' : 'Expand this group to see every option'}>
                    <td colSpan={7} className="px-5 py-2">
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                        <button type="button" aria-expanded={isGroupOpen} className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-600" onClick={(e) => { e.stopPropagation(); toggleGroup(g.key); }}>
                          <span aria-hidden className="inline-block w-3 text-slate-400">{isGroupOpen ? '▾' : '▸'}</span>
                          {g.termYears ? `${g.termYears}-year` : 'No term'}
                          {g.loanKind ? ` · ${g.loanKind.label}` : ''} · {g.buydown ? `${g.buydown.label} buydown` : 'No buydown'}
                          <span className="font-normal normal-case tracking-normal text-slate-400">
                            {g.rows.length} option{g.rows.length === 1 ? '' : 's'}
                          </span>
                        </button>
                        {!isGroupOpen && best && (
                          <span className="text-xs text-slate-600">
                            <span className="text-slate-400">{best.starred ? 'Best rate' : 'Closest to the desired price'}:</span>{' '}
                            <span className="font-medium text-slate-800">{best.lender} · {best.product}</span>{' '}
                            <span className="font-semibold tabular-nums text-slate-800">
                              {fmtPct3(best.headline.rate)}
                              {best.starred ? <span className="ml-0.5 text-brand-gold-700">★</span> : null}
                            </span>{' '}
                            <span className="tabular-nums text-slate-500">@ {fmtPrice(best.headline.price)}</span>
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                  {isGroupOpen && g.rows.map((r) => (
                    <ResultRowGroup
                      key={r.optionId}
                      row={r}
                      isOpen={open.has(r.optionId)}
                      isBest={r.optionId === g.bestId && g.rows.length > 1}
                      onToggle={() => toggleOpen(r.optionId)}
                      loanAmount={r.kind === 'uwm' ? uwmLoanAmount : (sheetQuote?.scenario.loanAmount ?? 0)}
                      cypBps={cypBps}
                      uwmPoint={(p) => (uwm.status === 'ok' && r.uwmRow && p.uwm ? (uwm.result.validQuoteItems?.[r.uwmRow.quoteIndex]?.quotePricePoints?.[p.uwm.pointIndex] ?? null) : null)}
                      onAdd={(p) => (r.kind === 'uwm' ? onAddUwm(r.uwmRow!, p.uwm!) : onAddSheet(r.sheetRow!, p.sheet!))}
                      inTray={inTray}
                      quotedAt={uwm.status === 'ok' ? uwm.quotedAt : null}
                      lpCompPct={r.kind === 'uwm' ? uwmLpCompPct : null}
                      realtor={realtor}
                    />
                  ))}
                </Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div className="space-y-2 border-t border-slate-100 px-5 py-3">
        {ineligible.length === 0 ? (
          <p className="text-[11px] text-slate-400">Every product the picked lenders offer is eligible for this scenario.</p>
        ) : (
          <>
            <button type="button" onClick={() => setShowIneligible((v) => !v)} className="text-xs font-medium text-brand-navy-600 hover:underline">
              {showIneligible ? 'Hide' : 'Show'} {ineligible.length} ineligible product{ineligible.length === 1 ? '' : 's'}
            </button>
            {showIneligible && (
              <ul className="space-y-1 text-xs text-slate-600">
                {ineligible.map((r) => (
                  <li key={r.key}>
                    <span className="text-slate-500">{r.lender} ·</span> <span className="text-slate-800">{r.product}</span> <span className="text-slate-400">— {r.issue}</span>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
        {uwm.status === 'ok' && uwm.result.legalDisclaimer && <p className="pt-1 text-[11px] leading-relaxed text-slate-400">{uwm.result.legalDisclaimer}</p>}
      </div>
    </div>
  );
}

/** Pinning rates for the Realtor Marketing table from the ladders (rows that are one of its five programs). */
export interface RealtorControls {
  rowOf: (optionId: string) => RealtorRowKey | null;
  /** The row's current par price; null when nothing priced. (Informational since 2026-10-11: the point columns are true point charges, not measured from par.) */
  parPriceOf: (row: RealtorRowKey) => number | null;
  picks: RealtorPicks;
  onPick: (row: RealtorRowKey, column: RealtorColumnKey, optionId: string, rate: number) => void;
}

function RealtorPinButtons({ row, optionId, rate, price, realtor }: { row: RealtorRowKey; optionId: string; rate: number; price: number; realtor: RealtorControls }) {
  return (
    <span className="ml-1 inline-flex gap-0.5" title={`Use this rate in the Realtor Marketing email (${REALTOR_ROWS.find((r) => r.key === row)?.label})`}>
      {REALTOR_COLUMNS.map((col) => {
        const pick = realtor.picks[row]?.[col.key];
        const pinned = !!pick && pick.optionId === optionId && Math.abs(pick.rate - rate) < 1e-6;
        // Any rate can be the par rate; the point columns take rates whose total discount points are at most 1 / 2.
        const floor = col.key === 'par' ? null : realtorPriceFloor(col.key);
        const fits = floor == null || price >= floor;
        return (
          <button
            key={col.key}
            type="button"
            disabled={!fits && !pinned}
            onClick={(e) => {
              e.stopPropagation();
              realtor.onPick(row, col.key, optionId, rate);
            }}
            title={pinned ? `Pinned as the ${col.label} for ${REALTOR_ROWS.find((r) => r.key === row)?.label} — click to unpin` : fits ? (col.key === 'par' ? 'Pin as the par rate' : `Pin as the ${col.label} (a total charge of at most ${col.maxPoints} point${col.maxPoints === 1 ? '' : 's'})`) : `Costs more than ${col.maxPoints} point${col.maxPoints === 1 ? '' : 's'} in total — not a ${col.label}`}
            className={
              'rounded px-1 py-0.5 text-[10px] font-semibold ring-1 transition-colors disabled:cursor-not-allowed disabled:opacity-30 ' +
              (pinned ? 'bg-brand-gold-100 text-brand-gold-800 ring-brand-gold-300' : 'bg-white text-slate-500 ring-slate-200 hover:bg-slate-50')
            }
          >
            {col.short}
          </button>
        );
      })}
    </span>
  );
}

function ResultRowGroup({
  row,
  isOpen,
  isBest,
  onToggle,
  loanAmount,
  cypBps,
  uwmPoint,
  onAdd,
  inTray,
  quotedAt,
  lpCompPct,
  realtor,
}: {
  row: ResultRow;
  isOpen: boolean;
  isBest: boolean;
  onToggle: () => void;
  loanAmount: number;
  cypBps: number;
  uwmPoint: (p: ResultPoint) => UwmPricePoint | null;
  onAdd: (p: ResultPoint) => void;
  inTray: (optionId: string, rate: number) => boolean;
  quotedAt: string | null;
  /** UWM rows: the lender-paid comp percent netted out of UWM's price (null = borrower-paid). */
  lpCompPct: number | null;
  realtor?: RealtorControls;
}) {
  const [showAdj, setShowAdj] = useState<number | null>(null);
  const head = row.headline;
  const star = row.star;
  const bpBuydown = row.buydown?.paidBy === 'borrower';
  const isUwm = row.kind === 'uwm';
  const sheet = row.sheetRow;
  const feeTitle = sheet ? sheet.feeBreakdown.map((f) => `${f.label}: ${fmtMoney0(f.amount)}`).join('\n') : '';
  const ladderCols = 6 + (bpBuydown ? 1 : 0) + (isUwm ? 1 : 0);
  const realtorRow = realtor ? realtor.rowOf(row.optionId) : null;
  return (
    <>
      <tr className={'cursor-pointer border-t border-slate-100 ' + (isBest ? 'bg-brand-gold-50/70 hover:bg-brand-gold-50' : 'hover:bg-slate-50')} onClick={onToggle} title="Click to see every rate for this option">
        <td className="py-2 pl-5 pr-2 align-top">
          <div className="flex items-start gap-1.5">
            <span className="mt-0.5 inline-block w-3 text-slate-400">{isOpen ? '▾' : '▸'}</span>
            <div className="min-w-0">
              <p className="font-medium text-slate-800">
                {row.product}
                {isBest && (
                  <Badge tone="gold" className="ml-2 align-middle">
                    Best rate
                  </Badge>
                )}
              </p>
              <p className="text-[11px] text-slate-500">
                <span className={isUwm ? 'font-medium text-brand-gold-700' : 'font-medium text-teal-700'}>{row.lender}</span> · {row.lockPeriod}-day lock ·{' '}
                {isUwm ? (
                  <>live quote {fmtWhen(quotedAt)}</>
                ) : (
                  <span title={feeTitle} className="underline decoration-dotted">
                    fees {fmtMoney0(sheet?.lenderFees ?? 0)}
                  </span>
                )}
                {bpBuydown && head.buydownCost != null ? ` · buydown escrow ${fmtMoney0(head.buydownCost)}` : ''}
                {isUwm && head.mi ? ` · MI ${fmtMoney0(head.mi)}/mo` : ''}
              </p>
            </div>
          </div>
        </td>
        <td className="whitespace-nowrap px-2 py-2 text-right align-top tabular-nums">
          <span className={row.starred ? 'font-semibold text-slate-800' : 'font-medium text-slate-500'}>
            {fmtPct3(head.rate)}
            {row.starred && (
              <span className="ml-1 text-brand-gold-700" title="Best rate (★): meets your comp and closing-cost criteria">
                ★
              </span>
            )}
          </span>
          {!row.starred && (
            <span className="block text-[10px] font-normal text-slate-400" title="No rate on this option meets your ★ criteria">
              no ★ · desired price
            </span>
          )}
        </td>
        <td className="px-2 py-2 text-right align-top tabular-nums text-sky-700">{fmtPct3(head.apr)}</td>
        <td className={'px-2 py-2 text-right align-top font-semibold tabular-nums ' + (head.price >= 100 ? 'text-emerald-700' : 'text-rose-600')}>{fmtPrice(head.price)}</td>
        <td className="whitespace-nowrap px-2 py-2 text-right align-top">
          <CreditCell pct={head.discountPct} dollars={head.discountDollars} compact />
        </td>
        <td className="px-2 py-2 text-right align-top tabular-nums text-slate-800">{fmtMoney0(head.pi)}</td>
        <td className="py-2 pl-2 pr-4 text-right align-top">
          <AddButton added={inTray(row.optionId, head.rate)} onClick={() => onAdd(head)} />
        </td>
      </tr>
      {isOpen && (
        <tr className="bg-slate-50/60">
          <td colSpan={7} className="px-5 py-3">
            <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
              All rates · {row.lender} · {row.product}{row.termYears ? ` · ${row.termYears} yr` : ''}{row.buydown ? ` · ${row.buydown.label} buydown` : ''}
              <span className="ml-2 normal-case tracking-normal text-slate-400">click a rate for its pricing breakdown</span>
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-left text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                  <tr>
                    <th className="py-1 pr-3">Rate</th>
                    <th className="py-1 pr-3 text-right">Price</th>
                    <th className="py-1 pr-3 text-right">Cost / credit</th>
                    {bpBuydown && <th className="py-1 pr-3 text-right">Buydown escrow</th>}
                    <th className="py-1 pr-3 text-right">APR</th>
                    <th className="py-1 pr-3 text-right">P&amp;I</th>
                    {isUwm && <th className="py-1 pr-3 text-right">MI</th>}
                    <th className="py-1" />
                  </tr>
                </thead>
                <tbody>
                  {row.ladder.map((p) => {
                    const isStar = star != null && p.rate === star.rate;
                    // The desired-price point is only marked when no rate meets ★.
                    const isPick = !row.starred && p.rate === row.pick.rate;
                    const isOpenPoint = showAdj === p.rate;
                    const raw = isOpenPoint && isUwm ? uwmPoint(p) : null;
                    return (
                      <Fragment key={p.rate}>
                        <tr
                          className={
                            'cursor-pointer border-t border-slate-100 ' +
                            (isStar
                              ? 'bg-brand-gold-100/70 font-semibold text-brand-gold-800 hover:bg-brand-gold-100'
                              : isPick
                                ? 'bg-brand-navy-50/80 font-medium text-brand-navy-800 hover:bg-brand-navy-50'
                                : isOpenPoint
                                  ? 'bg-white'
                                  : 'hover:bg-white')
                          }
                          onClick={() => setShowAdj(isOpenPoint ? null : p.rate)}
                          title="Click for the pricing breakdown behind this rate"
                        >
                          <td className="py-1 pr-3 tabular-nums">
                            <span className="mr-1 inline-block w-2 text-slate-400">{isOpenPoint ? '▾' : '▸'}</span>
                            {fmtPct3(p.rate)}
                            {isStar && <span className="ml-1">★</span>}
                            {isPick && !isStar && <span className="ml-1 text-[10px] font-normal text-slate-400">desired price</span>}
                          </td>
                          <td className="py-1 pr-3 text-right tabular-nums">
                            {fmtPrice(p.price)}
                            {p.capped ? <span className="ml-1 text-[10px] text-slate-400">max</span> : null}
                            {p.sheet?.fromEngine ? (
                              <span className="ml-1 text-[10px] text-teal-700" title="A rate beyond the printed rate sheet — its base price comes from the lender's own pricing engine">
                                engine
                              </span>
                            ) : null}
                          </td>
                          <td className="py-1 pr-3 text-right">
                            <CreditCell pct={p.discountPct} dollars={p.discountDollars} compact />
                          </td>
                          {bpBuydown && <td className="py-1 pr-3 text-right tabular-nums text-slate-600">{p.buydownCost != null ? `(${fmtMoney0(p.buydownCost)})` : '—'}</td>}
                          <td className="py-1 pr-3 text-right tabular-nums">{fmtPct3(p.apr)}</td>
                          <td className="py-1 pr-3 text-right tabular-nums">{fmtMoney0(p.pi)}</td>
                          {isUwm && <td className="py-1 pr-3 text-right tabular-nums">{p.mi != null ? fmtMoney0(p.mi) : '—'}</td>}
                          <td className="py-1 text-right whitespace-nowrap">
                            <AddButton small added={inTray(row.optionId, p.rate)} onClick={() => onAdd(p)} />
                            {realtor && realtorRow && <RealtorPinButtons row={realtorRow} optionId={row.optionId} rate={p.rate} price={p.price} realtor={realtor} />}
                          </td>
                        </tr>
                        {isOpenPoint && (
                          <tr>
                            <td colSpan={ladderCols} className="px-1 pb-2 pt-1">
                              {isUwm ? (
                                raw ? (
                                  <UwmBreakdown point={raw} buydown={uwmBuydownForChoice(row.buydown)} loanAmount={loanAmount} cypBps={cypBps} lpCompPct={lpCompPct} buydownCost={p.buydownCost} buydownLabel={row.buydown?.label ?? null} />
                                ) : (
                                  <p className="text-[11px] text-slate-400">Breakdown unavailable.</p>
                                )
                              ) : (
                                <SheetBreakdown point={p.sheet!} loanAmount={loanAmount} buydown={row.buydown} />
                              )}
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

const fmtPct = (n?: number) => (typeof n === 'number' ? `${n.toFixed(3)}%` : '—');
const fmtAmt = (n?: number) =>
  typeof n === 'number' ? n.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2, currencySign: 'accounting' }) : '—';

/** UWM's pricing breakdown (the Pricing module's table): every adjustment in percent and dollars, plus the buydown LLPA, CYP and lender-paid comp lines. */
function UwmBreakdown({
  point,
  buydown,
  loanAmount,
  cypBps,
  lpCompPct,
  buydownCost,
  buydownLabel,
}: {
  point: UwmPricePoint;
  buydown: UwmBuydownSel | null;
  loanAmount: number;
  cypBps: number;
  lpCompPct: number | null;
  buydownCost: number | null;
  buydownLabel: string | null;
}) {
  const tb = uwmTempBuydownAdj(buydown, loanAmount);
  const cyp = uwmCypAdj(cypBps, loanAmount);
  const lp = uwmLpCompAdj(lpCompPct, loanAmount);
  const adjs = [...(point.adjustments ?? []), ...(tb ? [tb] : []), ...(cyp ? [cyp] : []), ...(lp ? [lp] : [])];
  const totalAdjPct = adjs.filter((a) => a.label !== 'Base Price').reduce((sum, a) => sum + (a.percent ?? 0), 0);
  const mi = point.mortgageInsurance;
  const final = uwmEffectiveFinal(point, buydown, loanAmount, cypBps, lpCompPct);
  return (
    <div className="grid grid-cols-1 gap-4 rounded-lg border border-slate-200 bg-white p-3 sm:grid-cols-3">
      <div className="sm:col-span-2">
        <p className="mb-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">UWM pricing breakdown</p>
        <table className="w-full text-xs">
          <thead className="text-[9px] uppercase tracking-wide text-slate-400">
            <tr>
              <th className="py-0.5 pr-2 text-left font-semibold">Adjustment</th>
              <th className="py-0.5 pr-2 text-right font-semibold">%</th>
              <th className="py-0.5 text-right font-semibold">Amount</th>
            </tr>
          </thead>
          <tbody>
            {adjs.map((a, i) => (
              <tr key={i} className="border-t border-slate-100">
                <td className="py-0.5 pr-2 text-slate-700">{a.label}</td>
                <td className="py-0.5 pr-2 text-right text-slate-600">{fmtPct(a.percent)}</td>
                <td className="py-0.5 text-right text-slate-700">{fmtAmt(a.amount)}</td>
              </tr>
            ))}
            <tr className="border-t-2 border-brand-navy-200 bg-brand-navy-50/70 font-semibold text-brand-navy-800">
              <td className="py-1 pr-2">Final Price</td>
              <td className="py-1 pr-2 text-right">{fmtPct(final?.percent)}</td>
              <td className="py-1 text-right">{fmtAmt(final?.amount)}</td>
            </tr>
          </tbody>
        </table>
        <p className="mt-1 text-[10px] text-slate-400">Shown in results as price {typeof final?.percent === 'number' ? fmtPrice(100 - final.percent) : '—'} (100 − final price %).</p>
        {buydown && !buydown.isLlpa && buydownCost != null && (
          <p className="mt-1 text-[11px] text-slate-600">
            {buydownLabel} buydown concession <span className="font-medium text-slate-800">{fmtMoney0(buydownCost)}</span> — a closing cost for ★, not in the price.
          </p>
        )}
      </div>
      <div>
        <p className="mb-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Product Fees</p>
        <dl className="space-y-1 text-xs">
          <div className="flex justify-between gap-2">
            <dt className="text-slate-500">Total Adjustment</dt>
            <dd className="text-slate-700">{fmtPct(totalAdjPct)}</dd>
          </div>
          <div className="flex justify-between gap-2">
            <dt className="text-slate-500">Mortgage Insurance</dt>
            <dd className="text-slate-700">
              {fmtPct(mi?.percent)} / {fmtAmt(mi?.amount)}
            </dd>
          </div>
        </dl>
        <p className="mt-2 text-[9px] italic text-slate-400">Does not include UWM Underwriting Fee.</p>
      </div>
    </div>
  );
}

/** A rate sheet's breakdown: base price, every price adjustment with its dollars, final price. */
function SheetBreakdown({ point, loanAmount, buydown }: { point: LadderPoint; loanAmount: number; buydown: BuydownChoice | null }) {
  const adjs = point.adjustments.filter((a) => Math.abs(a.value) >= 0.0005 || a.note);
  const total = point.adjustments.reduce((sum, a) => sum + a.value, 0);
  const base = point.price - total;
  const fmtDelta = (v: number) => `${v > 0 ? '+' : ''}${v.toFixed(3)}`;
  const fmtDollars = (pct: number) => {
    if (!(loanAmount > 0)) return '—';
    const d = (pct / 100) * loanAmount;
    if (Math.abs(d) < 0.5) return '$0';
    return d > 0 ? `+${fmtMoney0(d)}` : `(${fmtMoney0(Math.abs(d))})`;
  };
  const tone = (v: number) => (v < 0 ? 'text-rose-600' : v > 0 ? 'text-emerald-600' : 'text-slate-500');
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
      <p className="mb-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">
        Pricing breakdown at {fmtPct3(point.rate)}
        <span className="ml-2 normal-case tracking-normal text-slate-400">price points · + improves the price, − costs the borrower</span>
      </p>
      <table className="w-full text-xs">
        <thead className="text-[9px] uppercase tracking-wide text-slate-400">
          <tr>
            <th className="py-0.5 pr-2 text-left font-semibold">Adjustment</th>
            <th className="py-0.5 pr-2 text-right font-semibold">Price</th>
            <th className="py-0.5 text-right font-semibold">Amount</th>
          </tr>
        </thead>
        <tbody>
          <tr className="border-t border-slate-100">
            <td className="py-0.5 pr-2 text-slate-700">{point.fromEngine ? "Base price (lender's pricing engine — a rate beyond the printed sheet)" : 'Base price (rate sheet)'}</td>
            <td className="py-0.5 pr-2 text-right tabular-nums text-slate-600">{fmtPrice(base)}</td>
            <td className="py-0.5 text-right tabular-nums text-slate-500">{fmtDollars(base - 100)}</td>
          </tr>
          {adjs.length === 0 && (
            <tr className="border-t border-slate-100">
              <td colSpan={3} className="py-0.5 text-slate-400">
                No price adjustments apply to this scenario.
              </td>
            </tr>
          )}
          {adjs.map((a, i) => (
            <tr key={i} className="border-t border-slate-100">
              <td className="py-0.5 pr-2 text-slate-700">
                {a.label}
                {a.note ? <span className="ml-1 text-[10px] text-slate-400">{a.note}</span> : null}
              </td>
              <td className={'py-0.5 pr-2 text-right tabular-nums ' + tone(a.value)}>{fmtDelta(a.value)}</td>
              <td className={'py-0.5 text-right tabular-nums ' + tone(a.value)}>{fmtDollars(a.value)}</td>
            </tr>
          ))}
          <tr className="border-t border-slate-200 text-slate-600">
            <td className="py-0.5 pr-2">Total adjustments</td>
            <td className="py-0.5 pr-2 text-right tabular-nums">{fmtDelta(total)}</td>
            <td className="py-0.5 text-right tabular-nums">{fmtDollars(total)}</td>
          </tr>
          <tr className="border-t-2 border-brand-navy-200 bg-brand-navy-50/70 font-semibold text-brand-navy-800">
            <td className="py-1 pr-2">
              Final price
              {point.capped ? <span className="ml-1 text-[10px] font-normal text-slate-500">capped at the lender's max rebate</span> : null}
            </td>
            <td className="py-1 pr-2 text-right tabular-nums">{fmtPrice(point.price)}</td>
            <td className="py-1 text-right tabular-nums">{fmtDollars(point.price - 100)}</td>
          </tr>
        </tbody>
      </table>
      {buydown?.paidBy === 'borrower' && point.buydownCost != null && (
        <p className="mt-1.5 text-[11px] text-slate-600">
          {buydown.label} buydown escrow <span className="font-medium text-slate-800">{fmtMoney0(point.buydownCost)}</span> — funded at closing by the seller/borrower (not in the price); the lender credit has to cover it for ★.
        </p>
      )}
      {buydown?.paidBy === 'lender' && <p className="mt-1.5 text-[11px] text-slate-600">{buydown.label} buydown: the lender funds the escrow — its cost is the buydown line above, from this lender's rate sheet.</p>}
    </div>
  );
}
